/**
 * useUploadValidation - Validates and manages a single upload file
 *
 * Handles type, size, and dimension validation.
 * Returns file state with preview URL and metadata.
 */

import { useState, useCallback, useEffect } from 'react';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_SIZE_BYTES = 16 * 1024 * 1024;
const MIN_DIMENSION = 512;

export interface ValidatedFile {
  file: File;
  previewUrl: string;
  width: number;
  height: number;
}

interface UploadValidationState {
  validated: ValidatedFile | null;
  error: string | null;
  isValidating: boolean;
}

export function useUploadValidation() {
  const [state, setState] = useState<UploadValidationState>({
    validated: null, error: null, isValidating: false,
  });

  const clearFile = useCallback(() => {
    setState((prev) => {
      if (prev.validated) URL.revokeObjectURL(prev.validated.previewUrl);
      return { validated: null, error: null, isValidating: false };
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setState((prev) => {
        if (prev.validated) URL.revokeObjectURL(prev.validated.previewUrl);
        return prev;
      });
    };
  }, []);

  const validateAndSet = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setState({ validated: null, isValidating: false,
        error: 'Nur PNG, JPG und WebP sind erlaubt.' });
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      setState({ validated: null, isValidating: false,
        error: `Maximale Dateigroesse: 16 MB (aktuell: ${(file.size / 1024 / 1024).toFixed(1)} MB).` });
      return;
    }
    setState({ validated: null, error: null, isValidating: true });
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (img.width < MIN_DIMENSION || img.height < MIN_DIMENSION) {
        URL.revokeObjectURL(url);
        setState({ validated: null, isValidating: false,
          error: `Mindestgroesse: ${MIN_DIMENSION}x${MIN_DIMENSION}px (aktuell: ${img.width}x${img.height}px).` });
        return;
      }
      setState({
        validated: { file, previewUrl: url, width: img.width, height: img.height },
        error: null, isValidating: false,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      setState({ validated: null, isValidating: false,
        error: 'Bild konnte nicht gelesen werden.' });
    };
    img.src = url;
  }, []);

  return { ...state, validateAndSet, clearFile };
}
